import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Save, AlertTriangle, HardDrive } from "lucide-react";

import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Toast from "../../components/ui/Toast";
import ConfirmModal from "../../components/modals/ConfirmModal";
import useToast from "../../hooks/useToast";
import { monitoringService } from "../../api/monitoringService";
import { axiosPrivate } from "../../api/axios";

import OverridesPanel from "./components/OverridesPanel";
import { estimateDailyBytes, estimateMonthlyBytes, formatBytes } from "./utils/format";

const inputClass =
  "w-full px-3 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
      {label}
    </label>
    {children}
    {hint && (
      <p className="mt-1 text-[11px] text-gray-400 dark:text-light-500">{hint}</p>
    )}
  </div>
);

const MonitoringSettings = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();

  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await monitoringService.getSettings();
      setSettings(data.settings);
      setDraft(data.settings);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not load settings",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axiosPrivate
      .get("/api/employees")
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.employees || res.data?.records || [];
        setEmployees(list.filter((e) => !e.isSystemAccount));
      })
      .catch(() => setEmployees([]));
  }, []);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const dirty = useMemo(() => {
    if (!settings || !draft) return false;
    return Object.keys(draft).some(
      (k) => JSON.stringify(settings[k]) !== JSON.stringify(draft[k]),
    );
  }, [settings, draft]);

  const estimate = useMemo(() => {
    if (!draft) return null;
    const opts = {
      captureMode: draft.enabled ? draft.captureMode : "off",
      screenshotIntervalSeconds: draft.screenshotIntervalSeconds,
      videoBitrateKbps: draft.videoBitrateKbps,
    };
    return {
      daily: estimateDailyBytes(opts),
      monthly: estimateMonthlyBytes(opts),
    };
  }, [draft]);

  const persist = async () => {
    setSaving(true);
    try {
      const patch = {
        enabled: draft.enabled,
        captureMode: draft.captureMode,
        resolution: draft.resolution,
        fps: Number(draft.fps),
        chunkDuration: Number(draft.chunkDuration),
        videoBitrateKbps: Number(draft.videoBitrateKbps),
        screenshotIntervalSeconds: Number(draft.screenshotIntervalSeconds),
        screenshotQuality: Number(draft.screenshotQuality),
        screenshotResolution: draft.screenshotResolution,
        retentionDays: Number(draft.retentionDays),
        pauseOnScreenLock: draft.pauseOnScreenLock,
      };
      const data = await monitoringService.updateSettings(patch);
      setSettings(data.settings);
      setDraft(data.settings);
      showToast(
        data.versionBumped
          ? `Saved — desktop apps will pick this up (v${data.settings.version})`
          : "No changes to save",
        "success",
      );
    } catch (err) {
      showToast(
        err?.response?.data?.details?.[0] ||
          err?.response?.data?.message ||
          "Could not save settings",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    // Turning capture off entirely is worth a confirmation — it silently stops
    // collecting evidence for everyone.
    const turningOff =
      settings?.enabled && (!draft.enabled || draft.captureMode === "off");
    if (turningOff) setConfirmOff(true);
    else persist();
  };

  if (loading || !draft) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent" />
        </div>
      </PageWrapper>
    );
  }

  const videoActive =
    draft.enabled &&
    (draft.captureMode === "video" || draft.captureMode === "both");
  const shotsActive =
    draft.enabled &&
    (draft.captureMode === "screenshots" || draft.captureMode === "both");

  return (
    <PageWrapper
      title="Monitoring settings"
      subtitle="Applies to every employee unless a per-employee override is in force"
      actions={
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-lg"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save"}
        </Button>
      }
    >
      <button
        onClick={() => navigate("/management/monitoring")}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 dark:text-light-500 hover:text-primary-accent transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Monitoring
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* ── Capture ── */}
          <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
              Capture
            </h2>

            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <span>
                <span className="block text-sm text-gray-900 dark:text-light-100">
                  Capture enabled
                </span>
                <span className="block text-[11px] text-gray-400 dark:text-light-500">
                  Master switch. When off, time is still tracked but nothing is
                  captured.
                </span>
              </span>
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => set({ enabled: e.target.checked })}
                className="w-10 h-5 accent-primary-accent flex-shrink-0"
              />
            </label>

            <Field
              label="Capture mode"
              hint="Screenshots cost roughly 100x less bandwidth than video, which matters because employees upload over their own connections."
            >
              <select
                value={draft.captureMode}
                onChange={(e) => set({ captureMode: e.target.value })}
                disabled={!draft.enabled}
                className={inputClass}
              >
                <option value="screenshots">Screenshots (recommended)</option>
                <option value="video">Video</option>
                <option value="both">Video + screenshots</option>
                <option value="off">Off</option>
              </select>
            </Field>
          </div>

          {/* ── Screenshots ── */}
          <div
            className={`bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4 space-y-4 ${
              shotsActive ? "" : "opacity-50"
            }`}
          >
            <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
              Screenshots
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Interval (seconds)" hint="5–3600">
                <input
                  type="number"
                  min="5"
                  max="3600"
                  value={draft.screenshotIntervalSeconds}
                  onChange={(e) =>
                    set({ screenshotIntervalSeconds: e.target.value })
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Quality" hint="20–95; 60 keeps text legible">
                <input
                  type="number"
                  min="20"
                  max="95"
                  value={draft.screenshotQuality}
                  onChange={(e) => set({ screenshotQuality: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Resolution cap">
                <select
                  value={draft.screenshotResolution}
                  onChange={(e) =>
                    set({ screenshotResolution: e.target.value })
                  }
                  className={inputClass}
                >
                  <option value="640x360">640x360</option>
                  <option value="854x480">854x480</option>
                  <option value="1280x720">1280x720</option>
                  <option value="1920x1080">1920x1080</option>
                </select>
              </Field>
            </div>
          </div>

          {/* ── Video ── */}
          <div
            className={`bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4 space-y-4 ${
              videoActive ? "" : "opacity-50"
            }`}
          >
            <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
              Video
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Resolution cap">
                <select
                  value={draft.resolution}
                  onChange={(e) => set({ resolution: e.target.value })}
                  className={inputClass}
                >
                  <option value="640x360">640x360</option>
                  <option value="854x480">854x480</option>
                  <option value="1280x720">1280x720</option>
                  <option value="1920x1080">1920x1080</option>
                </select>
              </Field>
              <Field label="Frame rate" hint="1–60. Lower is dramatically cheaper.">
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={draft.fps}
                  onChange={(e) => set({ fps: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Bitrate (kbps)" hint="100–12000">
                <input
                  type="number"
                  min="100"
                  max="12000"
                  value={draft.videoBitrateKbps}
                  onChange={(e) => set({ videoBitrateKbps: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Chunk duration (seconds)" hint="30–1800">
                <input
                  type="number"
                  min="30"
                  max="1800"
                  value={draft.chunkDuration}
                  onChange={(e) => set({ chunkDuration: e.target.value })}
                  className={inputClass}
                />
              </Field>
            </div>
            <p className="text-[11px] text-gray-400 dark:text-light-500">
              The resolution is a <strong>downscale cap</strong>, not an exact size —
              aspect ratio is preserved, so a 16:10 display capped at 1280x720
              records at about 1106x720. Bitrate is only a target for the encoder.
            </p>
          </div>

          {/* ── Policy ── */}
          <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
              Policy
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Retention (days)"
                hint="Minimum 1. Media older than this is deleted automatically."
              >
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={draft.retentionDays}
                  onChange={(e) => set({ retentionDays: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <label className="flex items-start gap-2 cursor-pointer pt-6">
                <input
                  type="checkbox"
                  checked={draft.pauseOnScreenLock}
                  onChange={(e) => set({ pauseOnScreenLock: e.target.checked })}
                  className="mt-0.5 accent-primary-accent"
                />
                <span>
                  <span className="block text-sm text-gray-900 dark:text-light-100">
                    Pause on screen lock
                  </span>
                  <span className="block text-[11px] text-gray-400 dark:text-light-500">
                    The lock screen only yields black frames, so recording through it
                    wastes storage.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* ── Estimate + overrides ── */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <HardDrive className="w-4 h-4 text-primary-accent" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
                Estimated cost
              </h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-light-500">
                  Per employee / 8h day
                </span>
                <span className="font-semibold text-gray-900 dark:text-light-100">
                  {formatBytes(estimate.daily)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-light-500">
                  Per employee / month
                </span>
                <span className="font-semibold text-gray-900 dark:text-light-100">
                  {formatBytes(estimate.monthly)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-100 dark:border-dark-100">
                <span className="text-gray-500 dark:text-light-500">
                  20 employees / month
                </span>
                <span className="font-semibold text-gray-900 dark:text-light-100">
                  {formatBytes(estimate.monthly * 20)}
                </span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-gray-400 dark:text-light-500">
              Based on measured capture: a 720p still averages ~18 KB, and video
              lands near half its configured bitrate on screen content. This is
              <strong> uploaded from the employee's own connection</strong>, so it
              counts against their data allowance as well as your storage.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-900/40 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
                <p className="font-semibold">Changes are not instant</p>
                <p>
                  Desktop apps poll for settings, and apply a change at their next
                  capture boundary — up to about a minute plus the current chunk.
                  Turning capture off takes effect immediately.
                </p>
              </div>
            </div>
          </div>

          <OverridesPanel
            employees={employees}
            onChange={load}
            showToast={showToast}
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmOff}
        onClose={() => setConfirmOff(false)}
        onConfirm={() => {
          setConfirmOff(false);
          persist();
        }}
        title="Turn capture off?"
        message="No screenshots or recordings will be collected for anyone until it is switched back on. Work time will still be tracked."
        confirmText="Turn off"
        variant="danger"
      />

      <Toast {...toast} onClose={hideToast} />
    </PageWrapper>
  );
};

export default MonitoringSettings;
